import React, { useState, useRef, cloneElement, isValidElement, useCallback, ReactElement } from 'react'

import useOnClickOutside from '@/utils/useOnClickOutside'

import { Modal } from '../Modal'
import { StripedContainer } from '../StripedContainer'
import { Button } from './Button'
import { ExitButton } from './ExitButton'

import styles from './ConfirmButton.module.scss'

export const ConfirmButton: React.FC<{
  children: ReactElement<{ onClick: () => void }>
  onConfirm: () => void
  confirmText: string
  cancelText?: string
  titleText: string
  descriptionText?: string | JSX.Element
  confirmButtonClass?: string
  withCloseIcon?: boolean
  onClick?: () => void
}> = ({
  children,
  confirmText,
  cancelText,
  titleText,
  descriptionText,
  confirmButtonClass,
  withCloseIcon,
  onClick,
  onConfirm,
}) => {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const onClose = useCallback(() => {
    setOpen(false)
    onClick?.()
  }, [setOpen, onClick])
  useOnClickOutside(containerRef, onClose)
  return (
    <>
      {React.Children.map(children, (child) => {
        if (isValidElement(child)) {
          return cloneElement(child, {
            onClick: () => {
              setOpen(true)
            },
          })
        }
        return child
      })}
      {open && (
        <Modal onClose={() => null} position="Middle">
          <StripedContainer ref={containerRef} containerClass={styles.Container}>
            {withCloseIcon && <ExitButton className={styles.ExitButton} onClick={onClose} />}
            <div className={styles.Title}>{titleText}</div>
            <div className={styles.Description}>{descriptionText}</div>
            <div className={styles.Actions}>
              {cancelText && (
                <Button type="OutlineSecondary" data-cy="button-cancel" onClick={onClose}>
                  {cancelText}
                </Button>
              )}
              <Button
                additionalClasses={confirmButtonClass}
                type="Primary"
                data-cy="button-confirm"
                onClick={() => {
                  onConfirm()
                  onClose()
                }}
              >
                {confirmText}
              </Button>
            </div>
          </StripedContainer>
        </Modal>
      )}
    </>
  )
}
